import { QueryClient } from '@tanstack/react-query'
import { axios } from './common'
import { Role } from '@/acl'

// ========= api and dto section =========

export interface AuthUser {
  id: number
  username: string
  name: string
  roleNames: string[]
  isSuperuser: boolean
}

interface LoginResponse {
  id: number
  username: string
  name: string
  roleNames: string[]
  isSuperuser: boolean
}

export async function login(
  username: string,
  password: string
): Promise<AuthUser> {
  // Axios default behavior is to reject every response that returns with a status code
  // that falls out of the range of 2xx and treat it as an error.
  const resp = await axios.post<LoginResponse>('/api/auth/login', {
    username,
    password,
  })
  return resp.data
}

export async function refresh(): Promise<AuthUser> {
  const resp = await axios.get<LoginResponse>('/api/auth/refresh')
  return resp.data
}

export async function logout(): Promise<void> {
  await axios.post('/api/auth/logout')
}

//===== use in context =====

/**
 * this is the Auth object that will be used in the tanstack router context
 */
export type Auth = {
  login: (username: string, password: string) => Promise<AuthUser>
  logout: () => Promise<void>
  /*
   * this will refresh the user object
   * this is useful when the user is already logged in
   * and the page is refreshed
   */
  refresh: () => Promise<AuthUser>
  status: 'loggedOut' | 'loggedIn'
  user: AuthUser | undefined
  isSuperuser: boolean
  /*
   * this function will check if the user has the required role
   * if the user is not logged in, it will return false
   * if the requiredRoles is not provided, it will check if the user is superuser or admin
   */
  hasRequiredRole: (requiredRoles?: Role[]) => boolean
}

export const auth: Auth = {
  status: 'loggedOut',
  user: undefined,
  isSuperuser: false,
  login: async (username, password) => {
    const user = await login(username, password)
    auth.status = 'loggedIn'
    auth.user = user
    auth.isSuperuser = user.isSuperuser
    return user
  },
  logout: async () => {
    await logout()
    auth.status = 'loggedOut'
    auth.user = undefined
  },
  refresh: async () => {
    const user = await refresh()
    auth.status = 'loggedIn'
    auth.user = user
    auth.isSuperuser = user.isSuperuser
    return user
  },
  hasRequiredRole: (requiredRoles?: Role[]) => {
    if (auth.user === undefined) {
      return false
    }
    // By default, superuser or admin can access everythings,
    // so they are required in all cases
    const roles = [Role.ADMIN, Role.SUPER]
    if (requiredRoles) {
      roles.push(...requiredRoles)
    }
    return roles.some((role) => auth.user?.roleNames.includes(role))
  },
}

export interface MyRouterContext {
  queryClient: QueryClient
  auth: Auth | undefined
}
